/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  Bell,
  CalendarCheck,
  CheckCircle2,
  Cookie,
  FileText,
  GraduationCap,
  KeyRound,
  Lock,
  MailCheck,
  Megaphone,
  MessageCircle,
  PlayCircle,
  Send,
  ShieldCheck,
  Smartphone,
  Sparkles,
  UsersRound,
  UserRound,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./LandingPage.css";

const FEATURES = [
  {
    title: "Attendance",
    short: "Live registers, attendance trends and daily visibility for school teams.",
    detail:
      "Track attendance across classes, view daily patterns, identify follow-ups and give school leaders a clearer picture of attendance health.",
    icon: CalendarCheck,
    tone: "blue",
  },
  {
    title: "Behaviour",
    short: "Record positive behaviour, concerns and pupil behaviour history.",
    detail:
      "Capture positive recognition, concerns and behaviour patterns in a structured way so staff and leaders can respond with consistency.",
    icon: BarChart3,
    tone: "purple",
  },
  {
    title: "Parent App",
    short: "A dedicated mobile experience for linked parents and families.",
    detail:
      "Give parents one secure place to view updates, attendance, reports, absence requests and important school communication.",
    icon: Smartphone,
    tone: "cyan",
  },
  {
    title: "Parent Updates",
    short: "Send school notices, class updates and child-linked messages.",
    detail:
      "Keep parents informed with targeted updates, broadcast messages and child-linked notifications from one controlled communication space.",
    icon: MessageCircle,
    tone: "green",
  },
  {
    title: "Absence Reporting",
    short: "Secure parent absence submissions with admin review workflows.",
    detail:
      "Parents can submit absence information while school teams review, prioritise and record decisions through a safer admin process.",
    icon: MailCheck,
    tone: "orange",
  },
  {
    title: "Monthly Reports",
    short: "Generate attendance report PDFs for parent access and records.",
    detail:
      "Create branded monthly attendance reports, store them securely and make them available to the correct linked parent account.",
    icon: FileText,
    tone: "navy",
  },
  {
    title: "Staff Portal",
    short: "Teacher access, class ownership and staff workflows.",
    detail:
      "Give staff role-appropriate access to the tools they need while keeping admin control over classes, permissions and school-wide activity.",
    icon: GraduationCap,
    tone: "indigo",
  },
  {
    title: "Invite System",
    short: "Secure parent and staff onboarding without messy email chains.",
    detail:
      "Send structured invites, track status, manage accepted users and reduce manual onboarding friction for school teams.",
    icon: UsersRound,
    tone: "blue",
  },
  {
    title: "Security Centre",
    short: "MFA, sessions, audit visibility and account protection.",
    detail:
      "Support safer operations with security monitoring, session visibility, MFA readiness and controls designed for school review.",
    icon: ShieldCheck,
    tone: "purple",
  },
  {
    title: "Audit Logs",
    short: "Track important platform actions for operational accountability.",
    detail:
      "Record key activity such as invite actions, role changes, attendance actions, report generation and sensitive review workflows.",
    icon: Lock,
    tone: "green",
  },
  {
    title: "School Branding",
    short: "Use school identity across invites, reports and parent flows.",
    detail:
      "Apply school name, logo and brand colour across key experiences so communication feels official and trusted.",
    icon: Sparkles,
    tone: "cyan",
  },
  {
    title: "Broadcast Centre",
    short: "Controlled school-to-parent announcements from one place.",
    detail:
      "Publish important updates to the right audience and keep parents connected through a consistent school communication channel.",
    icon: Megaphone,
    tone: "orange",
  },
];

const TRUST_POINTS = [
  {
    title: "UK GDPR aligned",
    text: "Designed around data minimisation, school ownership and controlled processing.",
  },
  {
    title: "Role-based access",
    text: "Admin, teacher and parent experiences are separated by permissions.",
  },
  {
    title: "School isolation",
    text: "Users only access the records connected to their school and role.",
  },
  {
    title: "Audit-ready workflows",
    text: "Important actions can be recorded for operational accountability.",
  },
  {
    title: "ICO registered",
    text: "CoreLogic Systems Ltd is registered with the ICO for data protection accountability.",
  },
];

const LEGAL_LINKS = [
  {
    title: "Privacy",
    text: "How PresentPlus handles privacy and school data.",
    icon: ShieldCheck,
    path: "/privacy",
    action: "Read more",
  },
  {
    title: "Data Protection",
    text: "How school data is controlled, protected and processed.",
    icon: Lock,
    path: "/data-protection",
    action: "Read more",
  },
  {
    title: "Terms",
    text: "Service terms and acceptable use.",
    icon: FileText,
    path: "/terms",
    action: "Read more",
  },
  {
    title: "Cookies",
    text: "How browser storage supports login and performance.",
    icon: Cookie,
    path: "/cookies",
    action: "Read more",
  },
  {
    title: "Trust Centre",
    text: "Security, privacy and compliance information.",
    icon: ShieldCheck,
    path: "/trust",
    action: "Visit centre",
  },
];

const NAV = [
  { label: "Platform", target: "platform" },
  { label: "Security", target: "security" },
  { label: "Trust", target: "trust" },
  { label: "Contact", target: "contact" },
];

const REAL_APP_IMAGE = "/landing/presentplus-admin-overview.png";
const DEMO_EMAIL = "hello@presentplus.uk";
const DEMO_REQUEST_URL =
  import.meta.env.VITE_DEMO_REQUEST_URL ||
  "https://europe-west2-presentplus-prod.cloudfunctions.net/submitDemoRequest";

const ENTERPRISE_TRUST = [
  {
    title: "ICO registered",
    text: "Operated by CoreLogic Systems Ltd with UK data protection accountability in place.",
    icon: ShieldCheck,
  },
  {
    title: "UK GDPR aligned",
    text: "Designed around data minimisation, school control and clear processing boundaries.",
    icon: Lock,
  },
  {
    title: "School data isolation",
    text: "Multi-tenant school records are separated by school and role permissions.",
    icon: KeyRound,
  },
  {
    title: "Audit-ready workflows",
    text: "Key actions such as invites, register reviews and report access can be logged.",
    icon: FileText,
  },
];

const FOOTER_LINKS = [
  { label: "Privacy", path: "/privacy" },
  { label: "Data Protection", path: "/data-protection" },
  { label: "Terms", path: "/terms" },
  { label: "Cookies", path: "/cookies" },
  { label: "Trust Centre", path: "/trust" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [featurePage, setFeaturePage] = useState(0);
  const [demoForm, setDemoForm] = useState({
    name: "",
    school: "",
    email: "",
    phone: "",
    role: "",
    message: "",
    website: "",
  });
  const [demoStatus, setDemoStatus] = useState({ type: "idle", message: "" });

  const featureGroups = useMemo(() => {
    const groups = [];
    for (let i = 0; i < FEATURES.length; i += 4) {
      groups.push(FEATURES.slice(i, i + 4));
    }
    return groups;
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setFeaturePage((current) => (current + 1) % featureGroups.length);
    }, 4000);

    return () => window.clearInterval(timer);
  }, [featureGroups.length]);

  function scrollTo(id) {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

function openLogin() {
  window.location.href = "/login";
}

  function openDemoForm() {
    setMobileOpen(false);
    setDemoStatus({ type: "idle", message: "" });
    setDemoOpen(true);
  }

  function updateDemoField(field, value) {
    setDemoForm((current) => ({ ...current, [field]: value }));
  }

  async function submitDemoRequest(event) {
    event.preventDefault();

    if (demoStatus.type === "loading") return;

    setDemoStatus({ type: "loading", message: "Sending your request…" });

    try {
      const response = await fetch(DEMO_REQUEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: demoForm.name,
          school: demoForm.school,
          email: demoForm.email,
          phone: demoForm.phone,
          role: demoForm.role,
          focus: demoForm.message,
          website: demoForm.website,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result?.ok !== true) {
        throw new Error(result?.message || "We could not send your request. Please try again.");
      }

      setDemoStatus({
        type: "success",
        message: "Thank you — your demo request has been received. We’ll contact you shortly.",
      });

      setDemoForm({
        name: "",
        school: "",
        email: "",
        phone: "",
        role: "",
        message: "",
        website: "",
      });
    } catch (error) {
      console.error("Demo request failed:", error);
      setDemoStatus({
        type: "error",
        message:
          error?.message ||
          `We could not send your request. Please email ${DEMO_EMAIL} directly.`,
      });
    }
  }

  function openLegalPage(path) {
    setMobileOpen(false);
    window.location.assign(path);
  }

  return (
    <div className="ppx-page">
      <div className="ppx-bg-orb ppx-bg-orb-one" />
      <div className="ppx-bg-orb ppx-bg-orb-two" />

      <header className="ppx-nav">
        <div className="ppx-container ppx-nav-inner">
          <button type="button" className="ppx-brand" onClick={() => navigate("/")}>
            <img src="/presentplus-logo.png" alt="PresentPlus" />
            <span>PresentPlus</span>
          </button>

          <nav className="ppx-nav-links">
            {NAV.map((item) => (
              <button key={item.target} type="button" onClick={() => scrollTo(item.target)}>
                {item.label}
              </button>
            ))}
          </nav>

          <div className="ppx-nav-actions">
<a className="ppx-login-btn" href="/login">
  <UserRound size={17} />
  Login
</a>
            <button type="button" className="ppx-demo-btn" onClick={openDemoForm}>
              <CalendarCheck size={17} />
              Book demo
            </button>
          </div>

          <button
            type="button"
            className="ppx-mobile-menu-btn"
            aria-label="Open menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X size={22} /> : "☰"}
          </button>
        </div>

        {mobileOpen ? (
          <div className="ppx-mobile-panel">
            {NAV.map((item) => (
              <button key={item.target} type="button" onClick={() => scrollTo(item.target)}>
                {item.label}
              </button>
            ))}
            <button type="button" onClick={openLogin}>Login</button>
            <button type="button" onClick={openDemoForm}>Book demo</button>
          </div>
        ) : null}
      </header>

      <main>
        <section className="ppx-hero ppx-container">
          <div className="ppx-hero-copy">
            <div className="ppx-kicker">
              <ShieldCheck size={16} />
              Secure school operations platform
            </div>

            <h1>
              Run your school. <span>Protect</span> every child.
            </h1>

            <p>
              PresentPlus brings attendance, behaviour, parent communication, absence
              reporting, monthly reports and security oversight into one modern school platform.
            </p>

            <div className="ppx-hero-actions">
              <button type="button" className="ppx-demo-btn ppx-hero-demo" onClick={openDemoForm}>
                <CalendarCheck size={18} />
                Book a demo
              </button>

              <button type="button" className="ppx-watch-btn" onClick={() => scrollTo("platform")}>
                <PlayCircle size={18} />
                Explore platform
              </button>
            </div>

            <div className="ppx-proof-row">
              <span><CheckCircle2 size={15} /> Built for schools</span>
              <span><CheckCircle2 size={15} /> Parent-ready</span>
              <span><CheckCircle2 size={15} /> Secure by design</span>
              <span><CheckCircle2 size={15} /> ICO registered</span>
            </div>
          </div>

          <div className="ppx-real-app-stage" aria-label="PresentPlus real app preview">
            <div className="ppx-real-app-frame">
              <div className="ppx-real-app-toolbar">
                <span />
                <span />
                <span />
                <strong>PresentPlus Admin Console</strong>
              </div>
              <img src={REAL_APP_IMAGE} alt="PresentPlus admin dashboard preview" />
            </div>

            <div className="ppx-floating-app-note">
              <Bell size={18} />
              <span>Live school activity, parent updates and attendance oversight in one dashboard.</span>
            </div>
          </div>
        </section>

        <section id="platform" className="ppx-section ppx-container">
          <div className="ppx-section-head">
            <span>Platform modules</span>
            <h2>More than attendance. A complete school operating layer.</h2>
            <p>Explore each module and see how PresentPlus supports daily school operations.</p>
          </div>

          <div className="ppx-feature-carousel" aria-label="PresentPlus feature modules">
            <div
              className="ppx-feature-track"
              style={{ transform: `translateX(-${featurePage * 100}%)` }}
            >
              {featureGroups.map((group, groupIndex) => (
                <div className="ppx-feature-slide" key={`feature-group-${groupIndex}`}>
                  {group.map((feature) => {
                    const Icon = feature.icon;
                    return (
                      <button
                        type="button"
                        className="ppx-feature-card"
                        key={feature.title}
                        onClick={() => setSelectedFeature(feature)}
                      >
                        <div className={`ppx-feature-icon ${feature.tone}`}>
                          <Icon size={23} />
                        </div>
                        <h3>{feature.title}</h3>
                        <p>{feature.short}</p>
                        <span>
                          Learn more <ArrowRight size={14} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="ppx-carousel-dots" aria-label="Feature carousel pages">
            {featureGroups.map((_, index) => (
              <button
                type="button"
                key={`dot-${index}`}
                className={index === featurePage ? "active" : ""}
                onClick={() => setFeaturePage(index)}
                aria-label={`Show feature group ${index + 1}`}
              />
            ))}
          </div>
        </section>

        <section id="security" className="ppx-security ppx-container">
          <div className="ppx-security-visual">
            <div className="ppx-shield-planet">
              <div className="ppx-orbit orbit-one" />
              <div className="ppx-orbit orbit-two" />
              <ShieldCheck size={78} />
              <CheckCircle2 className="ppx-shield-check" size={30} />
            </div>
          </div>

          <div className="ppx-security-copy">
            <h2>Security built into everyday school operations.</h2>
            <p>
              PresentPlus helps schools control access, separate school data, monitor
              sensitive activity and keep parent-linked workflows protected.
            </p>

            <div className="ppx-cert-strip">
              <span><ShieldCheck size={15} /> UK GDPR aligned</span>
              <span><Lock size={15} /> Role-based access</span>
              <span><BarChart3 size={15} /> Audit visibility</span>
              <span><CheckCircle2 size={15} /> Secure cloud hosting</span>
            </div>
          </div>

          <div className="ppx-trust-grid">
            {TRUST_POINTS.map((item) => (
              <TrustItem key={item.title} title={item.title} text={item.text} />
            ))}
          </div>
        </section>

        <section id="trust" className="ppx-legal-grid ppx-container">
          {LEGAL_LINKS.map((item) => {
            const Icon = item.icon;
            return (
              <article className="ppx-legal-card" key={item.title}>
                <Icon size={30} />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <button type="button" onClick={() => navigate(item.path)}>
                  {item.action} <ArrowRight size={15} />
                </button>
              </article>
            );
          })}
        </section>

        <section className="ppx-enterprise-trust ppx-container" aria-label="PresentPlus trust and compliance">
          <div className="ppx-section-head">
            <span>Trust and compliance</span>
            <h2>School-ready controls without exposing technical architecture.</h2>
            <p>
              PresentPlus gives schools clear assurance: controlled access, audit visibility,
              signed document access, data minimisation and UK data protection accountability.
            </p>
          </div>

          <div className="ppx-enterprise-trust-grid">
            {ENTERPRISE_TRUST.map((item) => {
              const Icon = item.icon;
              return (
                <article className="ppx-enterprise-trust-card" key={item.title}>
                  <div>
                    <Icon size={24} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section id="contact" className="ppx-final-strip ppx-container">
          <div className="ppx-final-icon">
            <ShieldCheck size={27} />
          </div>
          <div>
            <h2>Built for education. Designed for trust.</h2>
            <p>
              PresentPlus is operated by CoreLogic Systems Ltd. ICO registered and built around
              secure, role-based school operations.
            </p>
          </div>
          <div className="ppx-final-actions">
            <button type="button" onClick={openDemoForm}>
              Book a demo <Send size={16} />
            </button>
            <button type="button" onClick={() => openLegalPage("/trust")}>
              Trust centre <ArrowRight size={16} />
            </button>
          </div>
        </section>
      </main>

      {demoOpen ? (
        <div className="ppx-modal-backdrop" role="presentation" onClick={() => setDemoOpen(false)}>
          <div
            className="ppx-demo-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Book a PresentPlus demo"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="ppx-modal-close" type="button" onClick={() => setDemoOpen(false)}>
              <X size={20} />
            </button>

            <span className="ppx-modal-eyebrow">Book a demo</span>
            <h2>See PresentPlus for your school</h2>
            <p>Send a demo request to PresentPlus and we will reply with next steps.</p>

            <form className="ppx-demo-form" onSubmit={submitDemoRequest}>
              {demoStatus.type !== "idle" ? (
                <div
                  role={demoStatus.type === "error" ? "alert" : "status"}
                  aria-live="polite"
                  style={{
                    marginBottom: 16,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border:
                      demoStatus.type === "error"
                        ? "1px solid #fecaca"
                        : demoStatus.type === "success"
                          ? "1px solid #bbf7d0"
                          : "1px solid #bfdbfe",
                    background:
                      demoStatus.type === "error"
                        ? "#fef2f2"
                        : demoStatus.type === "success"
                          ? "#f0fdf4"
                          : "#eff6ff",
                    color:
                      demoStatus.type === "error"
                        ? "#991b1b"
                        : demoStatus.type === "success"
                          ? "#166534"
                          : "#1e40af",
                    fontWeight: 700,
                  }}
                >
                  {demoStatus.message}
                </div>
              ) : null}

              <input
                type="text"
                name="website"
                value={demoForm.website}
                onChange={(e) => updateDemoField("website", e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", opacity: 0 }}
              />

              <div className="ppx-demo-grid">
                <label>
                  Full name
                  <input
                    value={demoForm.name}
                    onChange={(e) => updateDemoField("name", e.target.value)}
                    placeholder="Your name"
                    required
                  />
                </label>

                <label>
                  School name
                  <input
                    value={demoForm.school}
                    onChange={(e) => updateDemoField("school", e.target.value)}
                    placeholder="School name"
                    required
                  />
                </label>

                <label>
                  Work email
                  <input
                    type="email"
                    value={demoForm.email}
                    onChange={(e) => updateDemoField("email", e.target.value)}
                    placeholder="name@school.co.uk"
                    required
                  />
                </label>

                <label>
                  Phone number
                  <input
                    value={demoForm.phone}
                    onChange={(e) => updateDemoField("phone", e.target.value)}
                    placeholder="Optional"
                  />
                </label>

                <label>
                  Role
                  <select
                    value={demoForm.role}
                    onChange={(e) => updateDemoField("role", e.target.value)}
                    required
                  >
                    <option value="">Select role</option>
                    <option value="Headteacher">Headteacher</option>
                    <option value="School business manager">School business manager</option>
                    <option value="Attendance lead">Attendance lead</option>
                    <option value="IT / MIS lead">IT / MIS lead</option>
                    <option value="Other">Other</option>
                  </select>
                </label>

                <label>
                  What would you like to see?
                  <select
                    value={demoForm.message}
                    onChange={(e) => updateDemoField("message", e.target.value)}
                  >
                    <option value="">Choose a focus</option>
                    <option value="Attendance, parent app and monthly reports">
                      Attendance, parent app and monthly reports
                    </option>
                    <option value="Parent communication and absence reporting">
                      Parent communication and absence reporting
                    </option>
                    <option value="Security, audit logs and data protection">
                      Security, audit logs and data protection
                    </option>
                    <option value="Full platform walkthrough">Full platform walkthrough</option>
                  </select>
                </label>
              </div>

              <button
                type="submit"
                className="ppx-demo-submit"
                disabled={demoStatus.type === "loading"}
              >
                {demoStatus.type === "loading" ? "Sending…" : "Send demo request"}
                {demoStatus.type !== "loading" ? <Send size={17} /> : null}
              </button>

              <small>
                Your request is sent securely to PresentPlus. We use your details only to arrange the demonstration.
              </small>
            </form>
          </div>
        </div>
      ) : null}

      {selectedFeature ? (
        <div className="ppx-modal-backdrop" role="presentation" onClick={() => setSelectedFeature(null)}>
          <div className="ppx-feature-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button className="ppx-modal-close" type="button" onClick={() => setSelectedFeature(null)}>
              <X size={20} />
            </button>

            <div className={`ppx-feature-icon ${selectedFeature.tone}`}>
              {React.createElement(selectedFeature.icon, { size: 27 })}
            </div>

            <span>PresentPlus module</span>
            <h2>{selectedFeature.title}</h2>
            <p>{selectedFeature.detail}</p>

            <div className="ppx-modal-actions">
              <button type="button" className="ppx-demo-btn" onClick={() => setSelectedFeature(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TrustItem({ title, text }) {
  return (
    <div className="ppx-trust-item">
      <div>
        <ShieldCheck size={20} />
      </div>
      <span>
        <strong>{title}</strong>
        <small>{text}</small>
      </span>
    </div>
  );
}
